import type { ComponentPropsOptions } from "vue";
import type { GlobalOptionsSelect } from "@/components/helpers/types";
import type { SelectTriggerVariants } from "@/components/primitives/select/variants";
import { computed, defineComponent } from "vue";
import { useStringifiedOptions } from "@/components/helpers/types";
import Spinner from "@/components/Spinner";
import { p } from "@/utils/props-builder";
import { renderComponent } from "@/utils/render-component";
import { Select } from "../../primitives/select/Select";
import { fieldCtx } from "../field/Field";

const selectProps = {
  modelValue: p.custom<string | number>([String, Number]),
  options: p.array<GlobalOptionsSelect[]>([]),
  id: p.string(),
  required: p.boolean(),
  name: p.string(),
  size: p.string<"sm" | "md" | "lg">("md"),
  placeholder: p.string(),
  disabled: p.boolean(),
  position: p.string<"popper" | "item-aligned">("item-aligned"),
  onChange: p.func(),
  variant: p.string<SelectTriggerVariants["variant"]>("default"),
  side: p.string<"top" | "bottom">("bottom"),
  arrowIcon: p.string(),
  loading: p.boolean(),
} satisfies ComponentPropsOptions;

export default defineComponent({
  name: "Select",
  emits: ["update:modelValue", "change"],
  props: selectProps,
  setup(props) {
    const field = fieldCtx.inject(props);

    const { vModel, options } = useStringifiedOptions(props, "modelValue", () => props.options);

    const activeOption = computed(() =>
      options.value.find((option) => option.value === vModel.value)
    );

    const btnLabel = computed(() => {
      if (typeof activeOption.value?.btnLabel === "function") {
        return activeOption.value.btnLabel();
      }

      return activeOption.value?.btnLabel ?? activeOption.value?.label ?? "";
    });

    return () => (
      <div class="w-full">
        <Select.Root disabled={props.disabled} v-model={vModel.value}>
          <Select.Trigger
            arrowIcon={props.loading ? "" : props.arrowIcon}
            disabled={props.disabled}
            variant={props.variant}
            placeholder={props.placeholder}
            size={field.size ?? "md"}
            class="relative data-placeholder:text-muted-foreground"
          >
            {activeOption.value ? renderComponent(btnLabel.value) : undefined}
            {props.loading && (
              <div class="center absolute right-1.5 top-[7px] h-4 w-6 text-brand-9">
                <Spinner class="h-4 w-4" />
              </div>
            )}
          </Select.Trigger>
          <Select.Content position={props.position} side={props.side}>
            <Select.Group>
              {options.value
                // Drop orphaned separators (leading, trailing, consecutive).
                .filter((option, idx, arr) => {
                  if (!option.separator) return true;
                  if (idx === 0 || idx === arr.length - 1) return false;
                  return !arr[idx - 1]?.separator;
                })
                .map((option) => {
                  if (option.separator) {
                    return <Select.Separator key={String(option.value)} />;
                  }

                  const label = typeof option.label === "function" ? option.label() : option.label;

                  return (
                    <Select.Item
                      key={String(option.value)}
                      value={option.value as string}
                      disabled={option.disabled}
                      textValue={option.textValue}
                    >
                      {renderComponent(label)}
                    </Select.Item>
                  );
                })}
            </Select.Group>
          </Select.Content>
        </Select.Root>
      </div>
    );
  },
});
